export const VERSION = {
  app: "1.0.0",
  engine: "1.0.0",
  method: "1.0.0",
  report: "1.0.0",
  docs: "2026-10-04",
};
export const LIMITS = {
  files: 500,
  fileBytes: 2_000_000,
  totalBytes: 30_000_000,
  jsonDepth: 40,
  nodes: 30_000,
  valueLength: 100_000,
  rules: 30,
};
export type Status =
  | "concordant"
  | "ecart"
  | "non_determine"
  | "non_pris_en_charge"
  | "non_applicable"
  | "invalide";
export const LABELS: Record<Status, string> = {
  concordant: "Concordant",
  ecart: "Écart",
  non_determine: "Non déterminé",
  non_pris_en_charge: "Non pris en charge",
  non_applicable: "Non applicable",
  invalide: "Entrée / configuration invalide",
};
export class ControlError extends Error {
  constructor(
    public status: Status,
    message: string,
  ) {
    super(message);
  }
}
export type Input = {
  name: string;
  html: string;
  mode: "html_source" | "dom_snapshot";
  referenceUrl?: string;
  capturedAt?: string;
  provenance: string;
};
export type Selection = {
  types: string[];
  by: "page_url" | "id" | "unique";
  id?: string;
};
export type Rule = {
  id: string;
  label: string;
  reader: "date" | "authors" | "breadcrumb" | "faq";
  expected: boolean;
  scope: string;
  selector: string;
  source: "text" | "attribute";
  attribute?: string;
  cardinality: "one" | "set" | "sequence";
  entity: Selection;
  property: string;
  absence: "undetermined" | "not_applicable";
  absenceReason?: string;
  ambiguity: "undetermined";
  policy: {
    text: "space-presentation";
    urls: "conservative";
    dateFormat?: "iso" | "fr";
    order?: "set" | "sequence";
    lastUnlinked?: "both_absent" | "page_url";
    faqKey?: "question" | "identifier";
    links?: boolean;
  };
  fields?: {
    name?: string;
    url?: string;
    question?: string;
    answer?: string;
    keyAttribute?: string;
  };
};
export type Profile = {
  schemaVersion: "1.0";
  id: string;
  version: string;
  language: string;
  timezone?: string;
  referenceDate?: string;
  marker: string;
  pageUrl?: { selector: string; attribute: string };
  rules: Rule[];
};
export type Evidence = {
  script: number;
  pointer: string;
  nodeId?: string;
  value: unknown;
};
export type Finding = {
  rule_id: string;
  label: string;
  expected: boolean;
  status: Status;
  reason: string;
  selector: string;
  matchCount: number;
  htmlExcerpt: string[];
  htmlRaw: unknown;
  jsonRaw: unknown;
  htmlNormalized: unknown;
  jsonNormalized: unknown;
  provenance: Evidence[];
  comparison: string;
  limit: string;
  nextCheck: string;
};
export type PageReport = {
  file: string;
  inputMode: Input["mode"];
  referenceUrl?: string;
  capturedAt?: string;
  provenance: string;
  inputSha256: string;
  profile: { id: string; version: string; sha256: string };
  findings: Finding[];
  concordant: boolean;
  partial: boolean;
  scripts: number;
};
export type Report = {
  schemaVersion: string;
  versions: typeof VERSION;
  profile: Profile;
  profileSha256: string;
  generatedAt: string;
  limits: typeof LIMITS;
  pages: PageReport[];
  summary: {
    pages: number;
    concordant: number;
    differences: number;
    incomplete: number;
    findings: number;
    categoriesOverlap: true;
  };
};
export const fail = (status: Status, message: string): never => {
  throw new ControlError(status, message);
};
export const arr = (v: any): any[] =>
  v === undefined ? [] : Array.isArray(v) ? v : [v];
export const stable = (v: unknown): string =>
  JSON.stringify(v, (_k, value) =>
    value && !Array.isArray(value) && typeof value === "object"
      ? Object.fromEntries(
          Object.keys(value)
            .sort()
            .map((k) => [k, value[k]]),
        )
      : value,
  );
export async function sha256(value: string) {
  const b = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return Array.from(new Uint8Array(b), (x) =>
    x.toString(16).padStart(2, "0"),
  ).join("");
}
